import React, { useCallback, useState, useEffect, useRef } from 'react';
import classnames from 'classnames';
import moment from 'moment';
import _, { get, noop, isNil } from 'lodash';
import { Input, Row, Col, Select, Form, Modal, Divider } from 'antd';
import { useTranslation } from 'react-i18next';
import FormSwitch from 'src/components/form-switch';
import FormItem from 'src/components/form-item';
import './style.scss';
import { getProfileByLogin } from 'src/apis/autofill'

const { TextArea } = Input;
const { Option } = Select;

/**
 * Renders a form modal component.
 *
 * @param {Object} props - The props for the FormModal component.
 * @param {{}} props.deallocation - The deallocation object.
 * @param {Function} props.onClose - The callback function to handle the modal close event.
 * @param {Function} props.onSave - The callback function to handle the save button click event.
 * @param {Boolean} props.loading - Indicates if the form is in a loading state.
 * @param {Boolean} props.canEdit - Indicates if the form can be edited.
 * @param {{}} props.user - The logged user object.
 * @param {{}} props.positionMaps - The position maps list object.
 * @returns {JSX.Element} The rendered FormModal component.
 * @example
 *    <FormModal
            canEdit={canEdit}
            user={user}
            loading={formFetching}
            onSave={onSaveForm}
            onClose={() => setEditingData(null)}
            deallocation={editingData}
            positionMaps={positionMaps}
      />
 */
function FormModal({
	deallocation,
	onClose,
	onSave,
	loading,
	canEdit,
	user,
	positionMaps,
}) {
	const { t } = useTranslation();
	const firstUpdate = useRef(true);
	const gutter = 10;
	const requiredRule = {
		required: true,
		message: t('requiredField'),
	};
	const technologies = get(
		deallocation,
		'collaborator_deallocation.technologies',
		[]
	);
	const title =
		deallocation && deallocation.id ? t('editPeople') : t('addPeople');
	let index = 0;
	const [skills, setSkills] = useState(
		[{},{},{},{},{}].map(() => {
			return {
				id: technologies[index]?.id ?? '',
				skill: technologies[index]?.skill ?? '',
				rating: technologies[index]?.details?.rating ?? '',
				mainSkill: technologies[index++]?.details?.mainSkill ?? 'N',
			};
		})
	);

	const [isPCD, setIsPCD] = useState(false);
	const [isAutofilled, setIsAutofilled] = useState(false);
	const [data, setData] = useState(
		deallocation && deallocation.id
			? {
					id: deallocation.id,
					date: deallocation.date ? moment(deallocation.date) : '',
			  }
			: {
					personWithDisability: 'No',
			  }
	); // set default data
	const [autofillInfo, setAutofillInfo] = useState({});
	const [form] = Form.useForm();

	/**
	 * Call onClose method upon canceling action or closing modal.
	 */
	const onCancel = useCallback(() => {
		if (loading) {
			return;
		}

		onClose();
	}, [onClose, loading]);

	useEffect(() => {
		if (firstUpdate.current) {
			firstUpdate.current = false;
			return;
		}
	}, [data.date, form]);

	const handleLoginAutofill = async (login) => {
		if (data.login) {
			try {
				const response = await getProfileByLogin(login);
				const selected = response.data.result;

				if (selected) {
					const skills = selected.skills.sort().reverse();
					skills[0].mainSkill = 'Y';

					const updatedData = {
						login: selected.login,
						name: selected.name,
						role: selected.role,
						positionMap: selected.positionMap,
						gu: selected.gu,
						profile: selected.profile,
						alliance: selected.alliance,
						pdm: selected.pdm,
						businessPartner: selected.businessPartner,
						site: selected.site,
						englishLevel: selected.englishLevel,
						skillMain: skills[0]?.skill || '',
						skillMainRating: skills[0]?.rating || '',
						skillOne: skills[1]?.skill || '',
						skillRatingOne: skills[1]?.rating || '',
						skillTwo: skills[2]?.skill || '',
						skillRatingTwo: skills[2]?.rating || '',
						skillThree: skills[3]?.skill || '',
						skillRatingThree: skills[3]?.rating || '',
						skillFour: skills[4]?.skill || '',
						skillRatingFour: skills[4]?.rating || '',
					};

					setAutofillInfo(updatedData);
					setData(updatedData);
					setSkills(skills);
					form.setFieldsValue(updatedData);
					setIsAutofilled(true)
				} else {
					setIsAutofilled(false)
				}
			
			} catch (error) {
				setIsAutofilled(false);
				console.error('Error fetching responsible by login:', error);
			}
		}
	  };

	/**
	 * Call onSave function which creates a deallocation.
	 */
	const onClickSave = useCallback(() => {
		onSave({
			...data,
			skills,
		});
	}, [onSave, data, skills]);

	/**
	 * Set isPCD boolean value.
	 * @param {string} pcdInfo - it can be "Yes", "No" or other variations of "No".
	 */
	const handlePCD = (pcdInfo) => {
		pcdInfo === 'Yes' ? setIsPCD(true) : setIsPCD(false);
	};

	/**
	 * Define current values for each field, in case it's editing, or empty if it's new.
	 */
	const onChangeCollaborator = () => {
		const login = get(deallocation, 'collaborator_deallocation.login', '');
		const name = get(deallocation, 'collaborator_deallocation.name', '');
		const role = get(deallocation, 'collaborator_deallocation.role', '');
		const positionMap = get(
			deallocation,
			'collaborator_deallocation.positionMap',
			''
		);
		const englishLevel = get(
			deallocation,
			'collaborator_deallocation.englishLevel',
			''
		);
		const gu = get(deallocation, 'collaborator_deallocation.gu', '');
		const alliance = get(
			deallocation,
			'collaborator_deallocation.alliance',
			''
		);
		const pdm = get(deallocation, 'collaborator_deallocation.pdm', '');
		const businessPartner = get(
			deallocation,
			'collaborator_deallocation.businessPartner',
			''
		);
		const site = get(deallocation, 'collaborator_deallocation.site', '');
		const contractType = get(
			deallocation,
			'collaborator_deallocation.contractType',
			''
		);
		const client = get(deallocation, 'collaborator_deallocation.client', '');
		const cLob = get(deallocation, 'collaborator_deallocation.cLob', '');
		const description = get(deallocation, 'description', '');
		const personWithDisability = get(
			deallocation,
			'collaborator_deallocation.personWithDisability',
			''
		);
		const statusNonBillable = get(
			deallocation,
			'collaborator_deallocation.statusNonBillable',
			''
		);

		handlePCD(personWithDisability);

		form.setFieldsValue({
			login,
			name,
			role,
			positionMap,
			gu,
			alliance,
			englishLevel,
			pdm,
			businessPartner,
			site,
			contractType,
			client,
			cLob,
			description,
			statusNonBillable,
			skillMain: skills[0]?.skill || '',
			skillMainRating: skills[0]?.rating || '',
			skillOne: skills[1]?.skill || '',
			skillRatingOne: skills[1]?.rating || '',
			skillTwo: skills[2]?.skill || '',
			skillRatingTwo: skills[2]?.rating || '',
			skillThree: skills[3]?.skill || '',
			skillRatingThree: skills[3]?.rating || '',
			skillFour: skills[4]?.skill || '',
			skillRatingFour: skills[4]?.rating || '',
		});
	};

	/**
	 * Call submit() event.
	 */
	const onFormSubmit = useCallback(() => {
		form.submit();
	}, [form]);

	useEffect(() => {
		/**
		 * If deallocation object is empty, call onChangeCollaborator method.
		 */
		if (!_.isEmpty(deallocation)) {
			onChangeCollaborator();
		}
	}, []);

	const isNewRegister = isNil(deallocation.id); // check if deallocationId exists (that is, in case it's editing)
	const isOwner =
		get(deallocation, 'collaborator_deallocation.pdm') === user.login;
	const isReadOnly = !isNewRegister && !canEdit && !isOwner;
	const modalClass = classnames('profile-form', {
		'is-read-only': isReadOnly,
	});

	/**
	 * Set a value and add it to data state.
	 * @param {string} prop - key
	 * @param {any} value - value
	 */
	const setValue = (prop, value) => {
		setData({
			...data,
			[prop]: value,
		});
	};

	return (
		<Modal
			confirmLoading={loading}
			title={title}
			className={modalClass}
			onCancel={onCancel}
			onOk={onFormSubmit}
			okButtonProps={{
				className: 'cit-btn link-type save-button',
			}}
			cancelButtonProps={{
				className: 'cit-btn link-type cancel-button',
			}}
			width={1000}
			visible={true}
			okText={t('save')}
			cancelText={t('cancel')}
			aria-modal={true}
			aria-label={title}
		>
			<Form
				data-testid='deallocation-form'
				form={form}
				id='deallocation-form'
				initialValues={data}
				onFinish={onClickSave}
			>
				<Row gutter={gutter}>
					<Col span={12}>
						<FormItem label='Login' id='login'>
							<Form.Item rules={[requiredRule]} name='login'>
								<Input
									disabled={isReadOnly || deallocation.id}
									placeholder={t('enterCI&TerLogin')}
									onChange={(e) => setValue('login', e.target.value)}
									aria-required={true}
									aria-label={t('enterCI&TerLogin')}
									aria-labelledby='login'
									onBlur={async () => {
										  await handleLoginAutofill(data.login);
									  }}
								/>
							</Form.Item>
						</FormItem>
					</Col>
					<Col span={12}>
						<FormItem label={t('name')} id='name'>
							<Form.Item
								rules={[requiredRule]}
								name='name'
								validateStatus={isAutofilled && autofillInfo.name !== data.name ? 'warning' : ''}
								help={isAutofilled && autofillInfo.name !== data.name ? t('nameDifferentFromAutofill') : ''}
								hasFeedback
							>
								<Input
									placeholder={t('enterCollaboratorName')}
									onChange={(e) => setValue('name', e.target.value)}
									aria-required={true}
									aria-label={t('enterCollaboratorName')}
									aria-labelledby='name'
								/>
							</Form.Item>
						</FormItem>
					</Col>
				</Row>
				<Row gutter={gutter}>
					<Col span={12}>
						<FormItem label={t('role')} id='role'>
							<Form.Item 
								rules={[requiredRule]}
								name='role'
								validateStatus={isAutofilled && autofillInfo.role !== data.role ? 'warning' : ''}
								help={isAutofilled && autofillInfo.role !== data.role ? t('roleDifferentFromAutofill') : ''}
								hasFeedback
							>
								<Input
									onChange={(e) => setValue('role', e.target.value)}
									placeholder={t('enterCollaboratorRole')}
									aria-required={true}
									aria-label={t('enterCollaboratorRole')}
									aria-labelledby='role'
								/>
							</Form.Item>
						</FormItem>
					</Col>
					<Col span={12}>
						<FormItem label={t('positionMap')} id='positionMapFormItem'>
							<Form.Item
								rules={[requiredRule]}
								name='positionMap'
								id='positionMapFormItemInput'
								aria-labelledby='positionMapFormItem'
								validateStatus={isAutofilled && autofillInfo.positionMap !== data.positionMap ? 'warning' : ''}
								help={isAutofilled && autofillInfo.positionMap !== data.positionMap ? t('positionMapDifferentFromAutofill') : ''}
								hasFeedback
							>
								<Select
									disabled={isReadOnly}
									onChange={(e) => setValue('positionMap', e)}
									placeholder={t('enterCollaboratorLevel')}
									aria-required={true}
									aria-label={t('enterCollaboratorLevel')}
									aria-labelledby='positionMapFormItem'
									mode={'single'}
									options={positionMaps}
								/>
							</Form.Item>
						</FormItem>
					</Col>
				</Row>
				<Divider className='profile-form__divider' />
				<Row gutter={gutter}>
					<Col span={18}>
						<FormItem label={t('mainSkill')} id='mainSkill'>
							<Form.Item
								name='skillMain'
								validateStatus={isAutofilled && skills[0] && autofillInfo.skillMain !== skills[0]?.skill ? 'warning' : ''}
								help={isAutofilled && skills[0] && autofillInfo.skillMain !== skills[0]?.skill ? t('skillMainDifferentFromAutofill') : ''}
								hasFeedback
							>
								<Input
									onChange={(e) => (
										skills[0] = {
											id: '',
											skill: e.target.value,
											mainSkill: 'Y',
											rating: skills[0]?.rating ?? 1,
										})
									}
									placeholder={t('enterCollaboratorMainTech')}
									aria-label={t('enterCollaboratorMainTech')}
									aria-labelledby='mainSkill'
								/>
							</Form.Item>
						</FormItem>
					</Col>
					<Col span={6}>
						<FormItem label={t('level')} id='skillMainRatingFormItem'>
							<Form.Item
								name='skillMainRating'
								id='skillMainRatingFormItemInput'
								aria-labelledby='skillMainRatingFormItem'
								validateStatus={isAutofilled && skills[0] && autofillInfo.skillMainRating !== skills[0]?.rating ? 'warning' : ''}
								help={isAutofilled && skills[0] && autofillInfo.skillMainRating !== skills[0]?.rating ? t('skillMainRatingDifferentFromAutofill') : ''}
								hasFeedback
							>
								<Select
									placeholder='Ex.: 1'
									onChange={(value) => (skills[0].rating = value)}
									aria-label={t('enterCollaboratorMainTechLevel')}
									aria-labelledby='skillMainRatingFormItem'
								>
									<Option value={1}>1</Option>
									<Option value={2}>2</Option>
									<Option value={3}>3</Option>
									<Option value={4}>4</Option>
									<Option value={5}>5</Option>
									<Option value={6}>6</Option>
									<Option value={7}>7</Option>
									<Option value={8}>8</Option>
								</Select>
							</Form.Item>
						</FormItem>
					</Col>
				</Row>
				<div className='profile-form__subtitle'>{t('otherTech')}</div>
				<Row gutter={gutter}>
					<Col span={8}>
						<FormItem label={t('tech')} id='skillOne'>
							<Form.Item
								name='skillOne'
								validateStatus={isAutofilled && skills[1] && autofillInfo.skillOne !== skills[1]?.skill ? 'warning' : ''}
								help={isAutofilled && skills[1] && autofillInfo.skillOne !== skills[1]?.skill ? t('skillOneDifferentFromAutofill') : ''}
								hasFeedback
							>
								<Input
									onChange={(e) =>
										(skills[1] = {
											id: '',
											skill: e.target.value,
											mainSkill: 'N',
											rating: skills[1]?.rating ?? 1,
										})
									}
									placeholder={t('enterCollaboratorTech')}
									aria-label={t('enterCollaboratorTech')}
									aria-labelledby='skillOne'
								/>
							</Form.Item>
						</FormItem>
					</Col>
					<Col span={4}>
						<FormItem label={t('level')} id='skillRatingOneFormItem'>
							<Form.Item
								name='skillRatingOne'
								id='skillRatingOneFormItemInput'
								aria-labelledby='skillRatingOneFormItem'
								validateStatus={isAutofilled && skills[1] && autofillInfo.skillRatingOne !== skills[1]?.rating ? 'warning' : ''}
								help={isAutofilled && skills[1] && autofillInfo.skillRatingOne !== skills[1]?.rating ? t('skillRatingOneDifferentFromAutofill') : ''}
								hasFeedback
							>
								<Select
									placeholder='Ex.: 1'
									onChange={(value) => (skills[1].rating = value)}
									aria-label={t('enterCollaboratorSkillLevel')}
									aria-labelledby='skillRatingOneFormItem'
								>
									<Option value={1}>1</Option>
									<Option value={2}>2</Option>
									<Option value={3}>3</Option>
									<Option value={4}>4</Option>
									<Option value={5}>5</Option>
									<Option value={6}>6</Option>
									<Option value={7}>7</Option>
									<Option value={8}>8</Option>
								</Select>
							</Form.Item>
						</FormItem>
					</Col>
					<Col span={8}>
						<FormItem label={t('tech')} id='skillTwo'>
							<Form.Item
								name='skillTwo'
								validateStatus={isAutofilled && skills[2] && autofillInfo.skillTwo !== skills[2]?.skill ? 'warning' : ''}
								help={isAutofilled && skills[2] && autofillInfo.skillTwo !== skills[2]?.skill ? t('skillTwoDifferentFromAutofill') : ''}
								hasFeedback
							>
								<Input
									onChange={(e) =>
										(skills[2] = {
											id: '',
											skill: e.target.value,
											mainSkill: 'N',
											rating: skills[2]?.rating ?? 1,
										})
									}
									placeholder={t('enterCollaboratorTech')}
									aria-label={t('enterCollaboratorTech')}
									aria-labelledby='skillTwo'
								/>
							</Form.Item>
						</FormItem>
					</Col>
					<Col span={4}>
						<FormItem label={t('level')} id='skillRatingTwoFormItem'>
							<Form.Item
								name='skillRatingTwo'
								id='skillRatingTwoFormItemInput'
								aria-labelledby='skillRatingTwoFormItem'
								validateStatus={isAutofilled && skills[2] && autofillInfo.skillRatingTwo !== skills[2]?.rating ? 'warning' : ''}
								help={isAutofilled && skills[2] && autofillInfo.skillRatingTwo !== skills[2]?.rating ? t('skillRatingTwoDifferentFromAutofill') : ''}
								hasFeedback
							>
								<Select
									placeholder='Ex.: 1'
									onChange={(value) => (skills[2].rating = value)}
									aria-label={t('enterCollaboratorSkillLevel')}
									aria-labelledby='skillRatingTwoFormItem'
								>
									<Option value={1}>1</Option>
									<Option value={2}>2</Option>
									<Option value={3}>3</Option>
									<Option value={4}>4</Option>
									<Option value={5}>5</Option>
									<Option value={6}>6</Option>
									<Option value={7}>7</Option>
									<Option value={8}>8</Option>
								</Select>
							</Form.Item>
						</FormItem>
					</Col>
				</Row>
				<Row gutter={gutter}>
					<Col span={8}>
						<FormItem label={t('tech')} id='skillThree'>
							<Form.Item
								name='skillThree'
								validateStatus={isAutofilled && skills[3] && autofillInfo.skillThree !== skills[3]?.skill ? 'warning' : ''}
								help={isAutofilled && skills[3] && autofillInfo.skillThree !== skills[3]?.skill ? t('skillThreeDifferentFromAutofill') : ''}
								hasFeedback
							>
								<Input
									onChange={(e) =>
										(skills[3] = {
											id: '',
											skill: e.target.value,
											mainSkill: 'N',
											rating: skills[3]?.rating ?? 1,
										})
									}
									placeholder={t('enterCollaboratorTech')}
									aria-label={t('enterCollaboratorTech')}
									aria-labelledby='skillThree'
								/>
							</Form.Item>
						</FormItem>
					</Col>
					<Col span={4}>
						<FormItem label={t('level')} id='skillRatingThreeFormItem'>
							<Form.Item
								name='skillRatingThree'
								id='skillRatingThreeFormItemInput'
								aria-labelledby='skillRatingThreeFormItem'
								validateStatus={isAutofilled && skills[3] && autofillInfo.skillRatingThree !== skills[3]?.rating ? 'warning' : ''}
								help={isAutofilled && skills[3] && autofillInfo.skillRatingThree !== skills[3]?.rating ? t('skillRatingThreeDifferentFromAutofill') : ''}
								hasFeedback
							>
								<Select
									placeholder='Ex.: 1'
									onChange={(value) => (skills[3].rating = value)}
									aria-label={t('enterCollaboratorSkillLevel')}
									aria-labelledby='skillRatingThreeFormItem'
								>
									<Option value={1}>1</Option>
									<Option value={2}>2</Option>
									<Option value={3}>3</Option>
									<Option value={4}>4</Option>
									<Option value={5}>5</Option>
									<Option value={6}>6</Option>
									<Option value={7}>7</Option>
									<Option value={8}>8</Option>
								</Select>
							</Form.Item>
						</FormItem>
					</Col>
					<Col span={8}>
						<FormItem label={t('tech')} id='skillFour'>
							<Form.Item
								name='skillFour'
								validateStatus={isAutofilled && skills[4] && autofillInfo.skillFour !== skills[4]?.skill ? 'warning' : ''}
								help={isAutofilled && skills[4] && autofillInfo.skillFour !== skills[4]?.skill ? t('skillFourDifferentFromAutofill') : ''}
								hasFeedback
							>
								<Input
									onChange={(e) =>
										(skills[4] = {
											id: '',
											skill: e.target.value,
											mainSkill: 'N',
											rating: skills[4]?.rating ?? 1,
										})
									}
									placeholder={t('enterCollaboratorTech')}
									aria-label={t('enterCollaboratorTech')}
									aria-labelledby='skillFour'
								/>
							</Form.Item>
						</FormItem>
					</Col>
					<Col span={4}>
						<FormItem label={t('level')} id='skillRatingFourFormItem'>
							<Form.Item
								name='skillRatingFour'
								id='skillRatingFourFormItemInput'
								aria-labelledby='skillRatingFourFormItem'
								validateStatus={isAutofilled && skills[4] && autofillInfo.skillRatingFour !== skills[4]?.rating ? 'warning' : ''}
								help={isAutofilled && skills[4] && autofillInfo.skillRatingFour !== skills[4]?.rating ? t('skillRatingFourDifferentFromAutofill') : ''}
								hasFeedback
							>
								<Select
									placeholder='Ex.: 1'
									onChange={(value) => (skills[4].rating = value)}
									aria-label={t('enterCollaboratorSkillLevel')}
									aria-labelledby='skillRatingFourFormItem'
								>
									<Option value={1}>1</Option>
									<Option value={2}>2</Option>
									<Option value={3}>3</Option>
									<Option value={4}>4</Option>
									<Option value={5}>5</Option>
									<Option value={6}>6</Option>
									<Option value={7}>7</Option>
									<Option value={8}>8</Option>
								</Select>
							</Form.Item>
						</FormItem>
					</Col>
				</Row>
				<Divider className='profile-form__divider' />
				<Row gutter={gutter}>
					<Col span={12}>
						<FormItem label='GU' id='gu'>
							<Form.Item
								name='gu'
								validateStatus={isAutofilled && autofillInfo.gu !== data.gu ? 'warning' : ''}
								help={isAutofilled && autofillInfo.gu !== data.gu ? t('guDifferentFromAutofill') : ''}
								hasFeedback
							>
								<Input
									disabled={isReadOnly}
									placeholder={t('enterCollaboratorGU')}
									onChange={(e) => setValue('gu', e.target.value)}
									aria-label={t('enterCollaboratorGU')}
									aria-labelledby='gu'
								/>
							</Form.Item>
						</FormItem>
					</Col>
					<Col span={12}>
						<FormItem label='Alliance' id='alliance'>
							<Form.Item
								name='alliance'
								validateStatus={isAutofilled && autofillInfo.alliance !== data.alliance ? 'warning' : ''}
								help={isAutofilled && autofillInfo.alliance !== data.alliance ? t('allianceDifferentFromAutofill') : ''}
								hasFeedback
							>
								<Input
									onChange={(e) => setValue('alliance', e.target.value)}
									placeholder={t('enterCollaboratorAlliance')}
									aria-label={t('enterCollaboratorAlliance')}
									aria-labelledby='alliance'
								/>
							</Form.Item>
						</FormItem>
					</Col>
				</Row>
				<Row gutter={gutter}>
					<Col span={12}>
						<FormItem label='PDM' id='pdm'>
							<Form.Item
								name='pdm'
								validateStatus={isAutofilled && autofillInfo.pdm !== data.pdm ? 'warning' : ''}
								help={isAutofilled && autofillInfo.pdm !== data.pdm ? t('pdmDifferentFromAutofill') : ''}
								hasFeedback
							>
								<Input
									disabled={isReadOnly}
									placeholder={t('enterCollaboratorPDM')}
									onChange={(e) => setValue('pdm', e.target.value)}
									aria-label={t('enterCollaboratorPDM')}
									aria-labelledby='pdm'
								/>
							</Form.Item>
						</FormItem>
					</Col>
					<Col span={12}>
						<FormItem label='Business Partner' id='bp'>
							<Form.Item
								name='businessPartner' 
								validateStatus={isAutofilled && autofillInfo.businessPartner !== data.businessPartner ? 'warning' : ''}
								help={isAutofilled && autofillInfo.businessPartner !== data.businessPartner ? t('businessPartnerDifferentFromAutofill') : ''}
								hasFeedback
							>
								<Input
									onChange={(e) => setValue('businessPartner', e.target.value)}
									placeholder={t('enterCollaboratorBP')}
									aria-label={t('enterCollaboratorBP')}
									aria-labelledby='bp'
								/>
							</Form.Item>
						</FormItem>
					</Col>
				</Row>
				<Row gutter={gutter}>
					<Col span={12}>
						<FormItem label={t('site')} id='site'>
							<Form.Item
								name='site' 
								validateStatus={isAutofilled && autofillInfo.site !== data.site ? 'warning' : ''}
								help={isAutofilled && autofillInfo.site !== data.site ? t('siteDifferentFromAutofill') : ''}
								hasFeedback
							>
								<Input
									onChange={(e) => setValue('site', e.target.value)}
									placeholder={t('enterCollaboratorSite')}
									aria-label={t('enterCollaboratorSite')}
									aria-labelledby='site'
								/>
							</Form.Item>
						</FormItem>
					</Col>
					<Col span={12}>
						<FormItem label={t('contractType')} id='contractType'>
							<Form.Item name='contractType'>
								<Input
									onChange={(e) => setValue('contractType', e.target.value)}
									placeholder={t('enterCollaboratorContractType')}
									aria-label={t('enterCollaboratorContractType')}
									aria-labelledby='contractType'
								/>
							</Form.Item>
						</FormItem>
					</Col>
				</Row>
				<Row gutter={gutter}>
					<Col span={12}>
						<FormItem label={t('client')} id='client'>
							<Form.Item name='client'>
								<Input
									onChange={(e) => setValue('client', e.target.value)}
									placeholder={t('enterCollaboratorClient')}
									aria-label={t('enterCollaboratorClient')}
									aria-labelledby='client'
								/>
							</Form.Item>
						</FormItem>
					</Col>
					<Col span={12}>
						<FormItem label='C-Lob' id='clob'>
							<Form.Item name='cLob'>
								<Input
									onChange={(e) => setValue('cLob', e.target.value)}
									placeholder={t('enterCollaboratorClob')}
									aria-label={t('enterCollaboratorClob')}
									aria-labelledby='clob'
								/>
							</Form.Item>
						</FormItem>
					</Col>
				</Row>
				<Row gutter={gutter}>
					<Col span={12}>
						<FormItem label={t('englishLevel')} id='englishLevel'>
							<Form.Item
								rules={[requiredRule]} name='englishLevel'
								validateStatus={isAutofilled && autofillInfo.englishLevel !== data.englishLevel ? 'warning' : ''}
								help={isAutofilled && autofillInfo.englishLevel !== data.englishLevel ? t('englishLevelDifferentFromAutofill') : ''}
								hasFeedback
							>
								<Input
									disabled={isReadOnly}
									onChange={(e) => setValue('englishLevel', e.target.value)}
									placeholder={t('enterCollaboratorEnglishLevel')}
									aria-required={true}
									aria-label={t('enterCollaboratorEnglishLevel')}
									aria-labelledby='englishLevel'
								/>
							</Form.Item>
						</FormItem>
					</Col>
					<Col span={12}>
						<FormItem id='personWithDisabilityFormItem'>
							<div className='profile-form__pwd'>
								<div className='profile-form__pwd_flex'>
									<FormSwitch
										defaultChecked={isPCD}
										name='personWithDisability'
										checkedChildren={t('yes')}
										unCheckedChildren={t('no')}
										onChange={(value) =>
											setValue('personWithDisability', value ? 'Yes' : 'No')
										}
										id='personWithDisabilitySwitch'
										aria-label={t('PWDForm')}
										aria-labelledby='personWithDisabilityFormItem'
									/>
									<span>{t('pwd')}</span>
								</div>
								<span className='profile-form__pwd_description'>
									{t('selectOptionEmployeesDisability')}
								</span>
							</div>
						</FormItem>
					</Col>
				</Row>
				<Divider className='profile-form__divider' />
				<Row gutter={gutter}>
					<Col span={24}>
						<FormItem label={t('notesOptional')} id='descriptionFormItem'>
							<Form.Item
								name='description'
								id='descriptionFormItemInput'
								aria-labelledby='descriptionFormItem'
							>
								<TextArea
									rows={6}
									disabled={isReadOnly}
									onChange={(e) => setValue('description', e.target.value)}
									aria-label={t('enterCollaboratorNotes')}
									aria-labelledby='descriptionFormItem'
								/>
							</Form.Item>
						</FormItem>
					</Col>
				</Row>
			</Form>
		</Modal>
	);
}

FormModal.defaultProps = {
	onSave: noop,
	onClose: noop,
	deallocation: {},
	loading: false,
	canEdit: false,
	user: {},
	positionMaps: {},
};

export default FormModal;
